import React, {
  useState, useEffect, useRef, useCallback,
  forwardRef, useImperativeHandle,
} from 'react';
import api from './api';

/* ── Constants ─────────────────────────────────────────────── */
const MIN_W    = 260;
const MIN_H    = 160;
const HEADER_H = 36;

/* ── Shared button style for header controls ───────────────── */
const HDR_BTN = {
  background: 'none', border: 'none',
  color: 'rgba(255,255,255,.65)', cursor: 'pointer',
  fontSize: 15, padding: '2px 6px', borderRadius: 4,
  lineHeight: 1, flexShrink: 0, display: 'inline-flex',
  alignItems: 'center',
};

/* ═══════════════════════════════════════════════════════════════
   SessionVideoPlayer
   Props:
     patientCard  – patient identifier
     onClose      – called when the ✕ button is clicked
     embedded     – (bool, default false) render inline instead of floating
     onPopOut     – called when embedded user clicks ⤢ pop-out
     onPopIn      – called when floating user clicks ⊟ pin-back
   Ref: exposes { pause(), play() }
═══════════════════════════════════════════════════════════════ */
const SessionVideoPlayer = forwardRef(function SessionVideoPlayer(
  { patientCard, onClose, embedded = false, onPopOut, onPopIn },
  ref,
) {
  /* ── Data ────────────────────────────────────────────────── */
  const [videos,     setVideos]     = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading,    setLoading]    = useState(true);

  /* ── Position / size (floating mode only) ────────────────── */
  const [pos,  setPos]  = useState(() => ({
    x: Math.max(20, window.innerWidth  - 370),
    y: 110,
  }));
  const [size, setSize] = useState({ w: 340, h: 210 });

  /* ── Refs ────────────────────────────────────────────────── */
  const playerRef   = useRef(null); // YT.Player instance
  const dragRef     = useRef(null); // { ox, oy } offset while dragging
  const resizeRef   = useRef(null); // { sx, sy, sw, sh } start values while resizing
  // Stable refs for use inside YT callbacks (avoid stale closures)
  const videosRef   = useRef([]);
  const curIdxRef   = useRef(0);
  // Unique stable div id so YT API can find the element
  const playerId    = useRef(`syvp-${Date.now()}`).current;

  useEffect(() => { videosRef.current  = videos;     }, [videos]);
  useEffect(() => { curIdxRef.current  = currentIdx; }, [currentIdx]);

  const driveVideoRef = useRef(null);

  /* ── Expose pause / play to parent ─────────────────────────
     Called by SessionIpadView.playDing() when exercise ding fires
  ────────────────────────────────────────────────────────────── */
  useImperativeHandle(ref, () => ({
    pause: () => {
      try { playerRef.current?.pauseVideo?.(); } catch {}
      try { driveVideoRef.current?.pause(); } catch {}
    },
    play: () => {
      try { playerRef.current?.playVideo?.(); } catch {}
      try { driveVideoRef.current?.play(); } catch {}
    },
  }));

  /* ── Fetch patient's assigned videos ─────────────────────── */
  useEffect(() => {
    if (!patientCard) return;
    setLoading(true);
    api.get(`/resource-videos/patient/${patientCard}`)
      .then(r => setVideos(r.data || []))
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  }, [patientCard]);

  /* ── Derived ─────────────────────────────────────────────── */
  const currentVideo = videos[currentIdx];
  const isDriveVideo = currentVideo?.source === 'drive';

  /* ── Init YouTube IFrame Player once videos are ready ──────
     Uses stable refs in onStateChange to avoid stale closure
     Only initialises if there are YouTube videos
  ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const ytVideos = videos.filter(v => v.source !== 'drive' && v.youtubeId);
    if (!ytVideos.length) return;
    // Don't init YT player if current video is a Drive video
    if (videos[0]?.source === 'drive') return;

    const initPlayer = () => {
      if (!document.getElementById(playerId)) return;
      try { playerRef.current?.destroy(); } catch {}

      playerRef.current = new window.YT.Player(playerId, {
        height:    '100%',
        width:     '100%',
        videoId:   ytVideos[0].youtubeId,
        playerVars: {
          autoplay:       1,
          controls:       1,
          rel:            0,
          modestbranding: 1,
          playsinline:    1,
          origin:         window.location.origin,
        },
        events: {
          onReady: e => {
            e.target.setVolume(18);
            e.target.playVideo();
          },
          onStateChange: e => {
            if (e.data === window.YT.PlayerState.ENDED) {
              const vids    = videosRef.current;
              const nextIdx = (curIdxRef.current + 1) % vids.length;
              setCurrentIdx(nextIdx);
              curIdxRef.current = nextIdx;
              const nextVid = vids[nextIdx];
              if (nextVid?.source === 'drive') return; // Drive videos handled by <video> tag
              try {
                e.target.loadVideoById(nextVid.youtubeId);
                e.target.setVolume(18);
              } catch {}
            }
          },
        },
      });
    };

    if (window.YT?.Player) {
      initPlayer();
    } else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === 'function') prev();
        initPlayer();
      };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const tag    = document.createElement('script');
        tag.src      = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);
      }
    }

    return () => {
      try { playerRef.current?.destroy(); playerRef.current = null; } catch {}
    };
  }, [videos, playerId]);

  /* ── Drag: start ────────────────────────────────────────── */
  const onHeaderMouseDown = useCallback(e => {
    if (e.button !== 0) return;
    dragRef.current = { ox: e.clientX - pos.x, oy: e.clientY - pos.y };
    e.preventDefault();
  }, [pos]);

  /* ── Resize: start ──────────────────────────────────────── */
  const onResizeMouseDown = useCallback(e => {
    if (e.button !== 0) return;
    resizeRef.current = { sx: e.clientX, sy: e.clientY, sw: size.w, sh: size.h };
    e.preventDefault();
    e.stopPropagation();
  }, [size]);

  /* ── Global mouse-move / up (drag + resize, floating only) ─ */
  useEffect(() => {
    if (embedded) return; // no drag/resize when embedded
    const onMove = e => {
      if (dragRef.current) {
        setPos({
          x: Math.max(0, Math.min(e.clientX - dragRef.current.ox,
              window.innerWidth  - size.w)),
          y: Math.max(0, Math.min(e.clientY - dragRef.current.oy,
              window.innerHeight - size.h - HEADER_H)),
        });
      }
      if (resizeRef.current) {
        const dx = e.clientX - resizeRef.current.sx;
        const dy = e.clientY - resizeRef.current.sy;
        setSize({
          w: Math.max(MIN_W,
              Math.min(resizeRef.current.sw + dx, window.innerWidth  * 0.85)),
          h: Math.max(MIN_H,
              Math.min(resizeRef.current.sh + dy, window.innerHeight * 0.80)),
        });
      }
    };
    const onUp = () => { dragRef.current = null; resizeRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
  }, [embedded, size]);

  /* ── Playlist navigation ─────────────────────────────────── */
  const goTo = useCallback(idx => {
    const vids = videosRef.current;
    if (!vids.length) return;
    const i = ((idx % vids.length) + vids.length) % vids.length;
    setCurrentIdx(i);
    curIdxRef.current = i;
    const nextVid = vids[i];
    if (nextVid?.source === 'drive') return; // Drive videos handled by <video> tag re-render
    try {
      playerRef.current?.loadVideoById(nextVid.youtubeId);
      playerRef.current?.setVolume(18);
    } catch {}
  }, []);

  /* ═══════════════════════════════════════════════════════════
     EMBEDDED MODE — compact inline player inside timer card
  ═══════════════════════════════════════════════════════════ */
  if (embedded) {
    return (
      <div style={{
        width: '100%', height: '100%',
        borderRadius: 10, overflow: 'hidden',
        background: '#0f172a',
        border: '1.5px solid rgba(255,255,255,.18)',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 4px 18px rgba(0,0,0,.50)',
        userSelect: 'none',
      }}>

        {/* ── Compact header ─────────────────────────────── */}
        <div style={{
          height: 26, display: 'flex', alignItems: 'center',
          padding: '0 5px', gap: 2, flexShrink: 0,
          background: 'linear-gradient(90deg, #1e1b4b, #0f172a)',
          borderBottom: '1px solid rgba(255,255,255,.07)',
        }}>
          <span style={{
            fontSize: 9.5, flex: 1, fontWeight: 600,
            color: 'rgba(255,255,255,.72)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            📺 {loading ? 'Loading…' : currentVideo?.title || 'No videos assigned'}
          </span>
          {videos.length > 1 && (
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,.30)', flexShrink: 0 }}>
              {currentIdx + 1}/{videos.length}
            </span>
          )}
          {videos.length > 1 && (
            <button
              style={{ ...HDR_BTN, fontSize: 13, padding: '2px 3px' }}
              title="Previous"
              onClick={() => goTo(currentIdx - 1)}
            >‹</button>
          )}
          {videos.length > 1 && (
            <button
              style={{ ...HDR_BTN, fontSize: 13, padding: '2px 3px' }}
              title="Next"
              onClick={() => goTo(currentIdx + 1)}
            >›</button>
          )}
          {/* Pop-out button */}
          {onPopOut && (
            <button
              style={{ ...HDR_BTN, fontSize: 13, padding: '2px 4px', color: 'rgba(255,255,255,.80)' }}
              title="Pop out player"
              onClick={onPopOut}
            >⤢</button>
          )}
          {/* Close */}
          <button
            style={{ ...HDR_BTN, color: '#f87171', fontSize: 13, padding: '2px 4px' }}
            title="Close player"
            onClick={onClose}
          >✕</button>
        </div>

        {/* ── Video area ───────────────────────────────── */}
        <div style={{ flex: 1, background: '#000', position: 'relative', minHeight: 0 }}>
          {loading ? (
            <Placeholder>Loading videos…</Placeholder>
          ) : videos.length === 0 ? (
            <Placeholder>
              No videos assigned.
              <small style={{ display: 'block', fontSize: 9, marginTop: 3 }}>Go to Resources → Assign</small>
            </Placeholder>
          ) : isDriveVideo ? (
            <video
              ref={driveVideoRef}
              key={currentVideo._id}
              src={process.env.REACT_APP_URL + '/resource-videos/stream/' + currentVideo._id}
              controls controlsList="nodownload"
              onContextMenu={e => e.preventDefault()}
              autoPlay playsInline
              onEnded={() => { if (videos.length > 1) goTo(currentIdx + 1); }}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <div id={playerId} style={{ width: '100%', height: '100%' }} />
          )}
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════
     FLOATING MODE — draggable / resizable PiP overlay
  ═══════════════════════════════════════════════════════════ */
  return (
    <div style={{
      position:     'fixed',
      left:         pos.x,
      top:          pos.y,
      width:        size.w,
      zIndex:       9800,
      borderRadius: 12,
      overflow:     'hidden',
      boxShadow:    '0 8px 40px rgba(0,0,0,.55)',
      background:   '#0f172a',
      border:       '1.5px solid rgba(255,255,255,.11)',
      userSelect:   'none',
    }}>

      {/* ── Header / drag handle ───────────────────────────── */}
      <div
        onMouseDown={onHeaderMouseDown}
        style={{
          height:       HEADER_H,
          display:      'flex',
          alignItems:   'center',
          padding:      '0 8px',
          gap:          5,
          cursor:       'grab',
          background:   'linear-gradient(90deg, #1e1b4b, #0f172a)',
          borderBottom: '1px solid rgba(255,255,255,.07)',
        }}
      >
        {/* Title */}
        <span style={{
          fontSize: 12, flex: 1, fontWeight: 600,
          color: 'rgba(255,255,255,.75)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          📺 {loading ? 'Loading…' : currentVideo?.title || 'No videos assigned'}
        </span>

        {/* N/M counter */}
        {videos.length > 1 && (
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', flexShrink: 0 }}>
            {currentIdx + 1}/{videos.length}
          </span>
        )}

        {/* Prev */}
        {videos.length > 1 && (
          <button
            style={HDR_BTN}
            title="Previous video"
            onMouseDown={e => e.stopPropagation()}
            onClick={() => goTo(currentIdx - 1)}
          >‹</button>
        )}

        {/* Next */}
        {videos.length > 1 && (
          <button
            style={HDR_BTN}
            title="Next video"
            onMouseDown={e => e.stopPropagation()}
            onClick={() => goTo(currentIdx + 1)}
          >›</button>
        )}

        {/* Pin back (embed) */}
        {onPopIn && (
          <button
            style={{ ...HDR_BTN, fontSize: 13 }}
            title="Pin back to timer"
            onMouseDown={e => e.stopPropagation()}
            onClick={onPopIn}
          >⊟</button>
        )}

        {/* Close */}
        <button
          style={{ ...HDR_BTN, color: '#f87171' }}
          title="Close player"
          onMouseDown={e => e.stopPropagation()}
          onClick={onClose}
        >✕</button>
      </div>

      {/* ── Video area ─────────────────────────────────────── */}
      <div style={{ height: size.h, background: '#000', position: 'relative' }}>
        {loading ? (
          <Placeholder>Loading videos…</Placeholder>
        ) : videos.length === 0 ? (
          <Placeholder>
            No videos assigned to this patient.
            <small style={{ marginTop: 4, display: 'block', fontSize: 11 }}>
              Go to Resources → Assign to patient
            </small>
          </Placeholder>
        ) : isDriveVideo ? (
          <video
            ref={driveVideoRef}
            key={currentVideo._id}
            src={process.env.REACT_APP_URL + '/resource-videos/stream/' + currentVideo._id}
            controls controlsList="nodownload"
            onContextMenu={e => e.preventDefault()}
            autoPlay playsInline
            onEnded={() => { if (videos.length > 1) goTo(currentIdx + 1); }}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <div id={playerId} style={{ width: '100%', height: '100%' }} />
        )}

        {/* Resize handle — bottom-right triangle */}
        <div
          onMouseDown={onResizeMouseDown}
          title="Drag to resize"
          style={{
            position:   'absolute', bottom: 0, right: 0,
            width:      22, height: 22, cursor: 'nwse-resize',
            background: 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,.22) 50%)',
            zIndex:     1,
          }}
        />
      </div>
    </div>
  );
});

/* ── Helper ──────────────────────────────────────────────────── */
function Placeholder({ children }) {
  return (
    <div style={{
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      height:         '100%',
      color:          'rgba(255,255,255,.42)',
      fontSize:       13,
      textAlign:      'center',
      padding:        16,
      gap:            4,
    }}>
      {children}
    </div>
  );
}

export default SessionVideoPlayer;
